import { inject, observer } from "mobx-react"
import { useStores } from "hooks/useStores"

import type _User from 'mobx/User'
import type _Infra from 'mobx/Infra'

import { codeToLocale, getLocaleStr, getTranslatedTextByKey } from 'utils/utils'

import {
    IconContainer, EditIcon, DeliveryIcon, PickupIcon, LocalizedContent as StyledLocalizedContent, OrderingIconContainer, OrderingIconText,
    LocalizedLeftContent, LocalizedLeftTextContainer, LocalizedLeftTextLine, LocalizedTextLabel, LocalizedLeftText
} from './style'
import { AddressLabel, SavedAddress } from "mobx/Localization/types"

const getAddressTitle = (address: SavedAddress) => {
	if (address.label === AddressLabel.Work) {
		return getTranslatedTextByKey('webviewFlow.localization.workLabel', 'Work')
	}

	if (address.label === AddressLabel.Home) {
		return getTranslatedTextByKey('webviewFlow.localization.homeLabel', 'Home')
	}

	return address.customLabel || getTranslatedTextByKey('webviewFlow.localization.otherLabel', 'Other')
}

const LocalizedContent = inject(
	'User',
    'Infra'
)(
    observer(({ User, Infra, displayOrderTypeIcon = true }: {User: typeof _User, Infra: typeof _Infra, displayOrderTypeIcon: boolean}) => {
        const { localizationStore } = useStores()
        const preferredLanguage = User?.preferredLanguage || 'en'
        const langFromInfra = Infra?.appParams?.l || 'en'
        const locale = User?.preferredLanguage ? codeToLocale[preferredLanguage] : codeToLocale[langFromInfra]
		    const savedAddress = localizationStore.actualFlow?.savedAddressId
			    ? localizationStore.getSavedAddress(localizationStore.actualFlow?.savedAddressId)
			    : null

        return (
            <StyledLocalizedContent onClick={localizationStore.edit}>
                <LocalizedLeftContent>
                    {displayOrderTypeIcon &&
                        <OrderingIconContainer data-testid="ordering-icon-container">
                            {localizationStore.actualFlow?.orderTypeFlow?.requireAddress ? <DeliveryIcon /> : <PickupIcon />}
                            <OrderingIconText variant="CTABig">{localizationStore.actualFlow?.orderTypeFlow && getTranslatedTextByKey(localizationStore.actualFlow.orderTypeFlow.orderTypeTranslationKey, localizationStore.actualFlow?.orderTypeFlow?.orderType)}</OrderingIconText>
                        </OrderingIconContainer>
                    }
                    <LocalizedLeftTextContainer data-testid="localized-left-text-container">
                        <LocalizedLeftTextLine  data-testid="localized-from-line">
                            <LocalizedTextLabel variant="BodySmallHighlighted">
                                {getTranslatedTextByKey('webviewFlow.localization.from', 'From')}:
                            </LocalizedTextLabel>
                            <LocalizedLeftText variant="BodySmall">
                                {localizationStore?.actualFlow?.store && getLocaleStr(localizationStore.actualFlow.store.name, locale)}
                            </LocalizedLeftText>
                        </LocalizedLeftTextLine>

                        <LocalizedLeftTextLine data-testid="localized-to-line">
                            <LocalizedTextLabel variant="BodySmallHighlighted">
                                {localizationStore.actualFlow?.orderTypeFlow?.requireAddress ? getTranslatedTextByKey('webviewFlow.localization.to', 'To') : getTranslatedTextByKey('webviewFlow.localization.storeAddress', 'Store Address')}
                                {!!savedAddress && ` ${getAddressTitle(savedAddress)}`}:
                            </LocalizedTextLabel>
                            <LocalizedLeftText variant="BodySmall">
                                {localizationStore.actualFlow?.address && localizationStore.actualFlow.address.formatted!}
                            </LocalizedLeftText>
                        </LocalizedLeftTextLine>
                    </LocalizedLeftTextContainer>
                </LocalizedLeftContent>
                <IconContainer>
                    <EditIcon />
                </IconContainer>
            </StyledLocalizedContent>
        )
    }
))

export default LocalizedContent
